import axios from 'axios';
import qs from 'qs';

const PAGE_SIZE_OPTIONS = [12, 24, 48];
const STORAGE_KEY = 'unified-search-auth-token';
const API_BASE = (import.meta.env.VUE_APP_V3_API_URL || 'http://localhost:3001').replace(/\/$/, '');
const API_KEY = import.meta.env.VUE_APP_V3_API_KEY || 'foobar';

const v3 = axios.create();
delete v3.defaults.headers.common['X-Authorization'];

function normalizeAuthToken(token = '') {
  const value = token.trim();
  if (!value) return '';
  return /^(JWT|Bearer)\s/i.test(value) ? value : `JWT ${value}`;
}

function emptyFilter() {
  return {};
}

function buildAndFilter(filter) {
  const and = {};

  Object.keys(filter || {}).forEach((key) => {
    const value = filter[key];
    if (Array.isArray(value) && value.length) {
      and[key] = value;
    } else if (value !== null && value !== undefined && value !== '' && !Array.isArray(value)) {
      and[key] = value;
    }
  });

  return Object.keys(and).length ? and : null;
}

export const initialState = () => ({
  authToken: localStorage.getItem(STORAGE_KEY) || '',
  queryParams: {
    limit: 12,
    offset: 0,
  },
  filter: emptyFilter(),
  search: '',
  list: [],
  banner: [],
  bannerTotal: 0,
  meta: {
    showMode: null,
    jatoEnabled: false,
  },
  serverPagination: {
    page: 1,
    rowsPerPage: 12,
    rowsNumber: null,
  },
  pageSizeOptions: PAGE_SIZE_OPTIONS,
  loading: false,
  error: null,
});

export const mutations = {
  SET_AUTH_TOKEN(state, token) {
    state.authToken = token || '';
  },

  SET_LIST(state, list) {
    state.list = Array.isArray(list) ? list : [];
  },

  SET_BANNER(state, banner = {}) {
    state.banner = Array.isArray(banner)
      ? banner
      : (Array.isArray(banner.data) ? banner.data : []);
    state.bannerTotal = Array.isArray(banner)
      ? banner.length
      : (banner.total || state.banner.length);
  },

  SET_META(state, meta = {}) {
    state.meta = {
      showMode: meta.showMode || null,
      jatoEnabled: Boolean(meta.jatoEnabled),
    };
  },

  SET_PAGINATION(state, { total, offset }) {
    // Keep the user-selected page size (12/24/48); never reset it from the response.
    const { limit } = state.queryParams;
    state.serverPagination.rowsNumber = total;
    state.serverPagination.rowsPerPage = limit;
    state.serverPagination.page = Math.floor((offset || 0) / limit) + 1;
    state.queryParams.offset = offset || 0;
  },

  SET_PAGE_SIZE(state, rowsPerPage) {
    state.queryParams.limit = rowsPerPage;
    state.queryParams.offset = 0;
    state.serverPagination.rowsPerPage = rowsPerPage;
    state.serverPagination.page = 1;
  },

  SET_PAGE(state, page) {
    const { limit } = state.queryParams;
    state.serverPagination.page = page;
    state.queryParams.offset = limit * (page - 1);
  },

  SET_FILTER(state, filter) {
    state.filter = { ...(filter || {}) };
  },

  SET_SEARCH(state, search) {
    state.search = search || '';
  },

  RESET_OFFSET(state) {
    state.queryParams.offset = 0;
    state.serverPagination.page = 1;
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  RESET(state) {
    const token = state.authToken;
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
    state.authToken = token;
  },
};

async function fetchVehicles({ authToken, queryParams, filter, search }) {
  const authorization = normalizeAuthToken(authToken);
  if (!authorization) {
    const error = new Error('NO_V3_JWT');
    error.code = 'NO_V3_JWT';
    throw error;
  }

  const params = {
    limit: queryParams.limit,
    offset: queryParams.offset,
  };

  const and = buildAndFilter(filter);
  if (and) params.and = and;
  if (search && search.trim().length >= 3) params.search = search.trim();

  return v3.get(`${API_BASE}/catalog/vehicles`, {
    params,
    paramsSerializer: p => qs.stringify(p, { arrayFormat: 'indices' }),
    headers: {
      Accept: 'application/json',
      'X-Api-Key': API_KEY,
      Authorization: authorization,
    },
    errorNotification: false,
  });
}

export const actions = {
  setAuthToken({ commit }, token) {
    const value = (token || '').trim();
    if (value) {
      localStorage.setItem(STORAGE_KEY, value);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    commit('SET_AUTH_TOKEN', value);
  },

  getUnifiedSearch({ commit, state }) {
    if (!state.authToken.trim()) {
      const error = new Error('NO_V3_JWT');
      error.code = 'NO_V3_JWT';
      commit('SET_ERROR', error);
      return Promise.reject(error);
    }

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    return fetchVehicles({
      authToken: state.authToken,
      queryParams: state.queryParams,
      filter: state.filter,
      search: state.search,
    })
      .then((res) => {
        const data = res.data || {};
        commit('SET_LIST', data.list);
        commit('SET_BANNER', data.banner);
        commit('SET_META', data.meta);
        commit('SET_PAGINATION', {
          total: data.total || 0,
          offset: data.offset != null ? data.offset : state.queryParams.offset,
        });
        commit('SET_LOADING', false);
      })
      .catch((error) => {
        commit('SET_LIST', []);
        commit('SET_BANNER', {});
        commit('SET_ERROR', error);
        commit('SET_LOADING', false);
        throw error;
      });
  },

  setPageSize({ commit, dispatch }, rowsPerPage) {
    commit('SET_PAGE_SIZE', rowsPerPage);
    return dispatch('getUnifiedSearch');
  },

  setPage({ commit, dispatch }, page) {
    commit('SET_PAGE', page);
    return dispatch('getUnifiedSearch');
  },

  applyFilter({ commit, dispatch }, { filter, search } = {}) {
    commit('SET_FILTER', filter || emptyFilter());
    commit('SET_SEARCH', search || '');
    commit('RESET_OFFSET');
    return dispatch('getUnifiedSearch');
  },

  resetFilter({ commit, dispatch }) {
    commit('SET_FILTER', emptyFilter());
    commit('SET_SEARCH', '');
    commit('RESET_OFFSET');
    return dispatch('getUnifiedSearch');
  },

  resetUnifiedSearch({ commit }) {
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state: initialState(),
  actions,
  mutations,
};
